"use client";

import React from "react";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Banknote, ChartBar, ChartPie, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
const chartData = [{
  month: "Jan",
  revenue: 4200,
  expenses: 1100
}, {
  month: "Feb",
  revenue: 4500,
  expenses: 1250
}, {
  month: "Mar",
  revenue: 4300,
  expenses: 900
}, {
  month: "Apr",
  revenue: 4800,
  expenses: 1400
}, {
  month: "May",
  revenue: 5100,
  expenses: 1050
}, {
  month: "Jun",
  revenue: 5400,
  expenses: 1150
}];
const chartConfig = {
  revenue: {
    label: "Revenue",
    color: "var(--chart-1)"
  },
  expenses: {
    label: "Expenses",
    color: "var(--chart-2)"
  }
};
const features = [{
  title: "Real-time ROI Tracking",
  description: "Monitor your investment performance instantly.",
  icon: TrendingUp,
  position: "lg:-left-16 lg:top-[15%]"
}, {
  title: "Direct Deposit Logs",
  description: "Track owner draws and incoming rent payments.",
  icon: Banknote,
  position: "lg:-right-20 lg:top-[30%]"
}, {
  title: "Detailed Expense Reports",
  description: "Transparent breakdown of all maintenance costs.",
  icon: ChartPie,
  position: "lg:-left-12 lg:bottom-[20%]"
}];
export default function PerformanceReporting() {
  return <section id="performance-reporting" className="relative overflow-hidden bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/5 via-background to-background py-24 sm:py-32">
      <div className="container mx-auto px-4 md:px-6">
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 flex items-center justify-center gap-2 text-primary">
            <ChartBar className="size-5" />
            <span className="text-sm font-semibold uppercase tracking-wider">
              Owner Portal
            </span>
          </div>
          <h2 className="mb-6 text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Financial Clarity at Your Fingertips
          </h2>
          <p className="mb-10 text-lg text-muted-foreground sm:text-xl">
            Experience complete transparency with our advanced owner portal.
            Monitor your Kansas City portfolio's performance, track real-time
            ROI, and access detailed financial reports 24/7.
          </p>
        </div>

        <div className="relative mx-auto mt-16 max-w-5xl">
          {}
          {features.map((feature, i) => <div key={i} data-index={i} className={`absolute z-30 hidden w-64 rounded-xl border border-border/50 bg-background/85 p-4 shadow-xl backdrop-blur-md lg:block ${feature.position}`}>
              <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <feature.icon className="size-5" />
              </div>
              <h3 className="mb-1 font-semibold text-foreground">
                {feature.title}
              </h3>
              <p className="text-sm text-muted-foreground">
                {feature.description}
              </p>
            </div>)}

          {}
          <div className="relative z-20 mx-auto w-full max-w-4xl">
            {}
            <div className="relative rounded-t-2xl border-[8px] border-muted bg-background p-2 shadow-2xl md:border-[16px] md:p-4">
              {}
              <div className="absolute top-1 left-1/2 size-1.5 -translate-x-1/2 rounded-full bg-muted-foreground/30 md:top-2 md:size-2"></div>
              
              {}
              <div className="aspect-[16/10] overflow-hidden rounded-lg border border-border bg-card p-4 sm:p-6">
                <div className="mb-6 flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-foreground">
                      Portfolio Performance
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      Year-to-Date Overview
                    </p>
                  </div>
                  <div className="hidden items-center gap-4 sm:flex">
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full bg-[var(--chart-1)]"></div>
                      <span className="text-sm text-muted-foreground">Revenue</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="size-3 rounded-full bg-[var(--chart-2)]"></div>
                      <span className="text-sm text-muted-foreground">Expenses</span>
                    </div>
                  </div>
                </div>
                
                <div className="h-[calc(100%-4rem)] w-full">
                  <ChartContainer config={chartConfig} className="h-full w-full">
                    <AreaChart data={chartData} margin={{
                    top: 10,
                    right: 10,
                    left: -20,
                    bottom: 0
                  }}>
                      <defs>
                        <linearGradient id="fillRevenue" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-revenue)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--color-revenue)" stopOpacity={0.0} />
                        </linearGradient>
                        <linearGradient id="fillExpenses" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="var(--color-expenses)" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="var(--color-expenses)" stopOpacity={0.0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/50" />
                      <XAxis dataKey="month" tickLine={false} axisLine={false} tickMargin={12} className="text-xs sm:text-sm" />
                      <YAxis tickLine={false} axisLine={false} tickMargin={12} tickFormatter={value => `$${value}`} className="text-xs sm:text-sm" />
                      <ChartTooltip cursor={false} content={<ChartTooltipContent />} />
                      <Area type="monotone" dataKey="revenue" stroke="var(--color-revenue)" strokeWidth={3} fillOpacity={1} fill="url(#fillRevenue)" />
                      <Area type="monotone" dataKey="expenses" stroke="var(--color-expenses)" strokeWidth={3} fillOpacity={1} fill="url(#fillExpenses)" />
                    </AreaChart>
                  </ChartContainer>
                </div>
              </div>
            </div>
            
            {}
            <div className="relative z-10 mx-auto h-4 w-[105%] rounded-b-2xl rounded-t-sm bg-muted shadow-xl md:h-6">
              <div className="mx-auto h-1 w-1/5 rounded-b-md bg-muted-foreground/20 md:h-1.5"></div>
            </div>
          </div>

          {}
          <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:hidden">
            {features.map((feature, i) => <Card key={i} data-index={i} className="bg-background/50 backdrop-blur-sm">
                <CardContent className="flex flex-col items-center p-6 text-center">
                  <div className="mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <feature.icon className="size-6" />
                  </div>
                  <h3 className="mb-2 text-lg font-semibold text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>)}
          </div>
        </div>

        <div className="mt-16 text-center sm:mt-20">
          <Link to="/ContactPage">
            <Button size="lg" className="px-8 text-base">
              Request a Property Management Consultation
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}